import { AnimatePresence, motion } from "framer-motion";
import {
  Fragment,
  type FC,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

export interface Tab {
  id: string;
  label: string;
  badgeCount?: number;
  group?: string;
  /** When true, the tab renders greyed and is not selectable. */
  disabled?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (id: string) => void;
  className?: string;
  ariaLabel?: string;
  /** Framer-motion layoutId for the active-underline animation. Override when
   *  multiple Tabs strips share a portal/layout root and need to be visually
   *  distinct. Defaults to a per-instance auto-generated id. */
  underlineLayoutId?: string;
}

function getDesktopTabTextClass(
  disabled: boolean | undefined,
  active: boolean,
) {
  if (disabled) {
    return "text-slate-300 dark:text-slate-700";
  }
  if (active) {
    return "text-emerald-600 dark:text-emerald-300";
  }
  return "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300";
}

function getDropdownTabClass(disabled: boolean | undefined, active: boolean) {
  if (disabled) {
    return "cursor-not-allowed text-slate-300 dark:text-slate-700";
  }
  if (active) {
    return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-200";
  }
  return "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800/70";
}

export const Tabs: FC<TabsProps> = ({
  tabs,
  activeTab,
  onChange,
  className = "",
  ariaLabel = "Tabs",
  underlineLayoutId,
}) => {
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const autoLayoutId = useId();
  const layoutId = underlineLayoutId ?? `hudTabUnderline-${autoLayoutId}`;

  useEffect(() => {
    const btn = buttonRefs.current[activeTab];
    if (!btn) {
      return;
    }
    btn.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [activeTab]);

  return (
    <div
      className={`relative border-b border-slate-300 bg-slate-100/60 dark:border-emerald-400/15 dark:bg-slate-950/60 ${className}`}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 hidden opacity-[0.04] dark:block"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,1) 0 1px, transparent 1px 3px)",
        }}
      />
      {/* Mobile: dropdown trigger. Horizontal-scroll tab strips are a
          discoverability failure on phone widths — replace with an explicit
          single-tap menu so all tabs are reachable without swiping. */}
      <TabsDropdown
        ariaLabel={ariaLabel}
        activeTab={activeTab}
        onChange={onChange}
        tabs={tabs}
      />
      {/* Desktop: existing horizontal strip with animated active underline. */}
      <div className="tabs-scroll-x relative hidden overflow-x-auto overflow-y-hidden sm:block">
        <nav
          className="flex w-max min-w-full gap-0.5 px-2 pt-1.5"
          aria-label={ariaLabel}
        >
          {tabs.map((tab, index) => {
            const active = activeTab === tab.id;
            const previous = tabs[index - 1];
            const showGroup = tab.group && tab.group !== previous?.group;
            return (
              <DesktopTab
                key={tab.id}
                tab={tab}
                active={active}
                showGroup={showGroup}
                buttonRefs={buttonRefs}
                onChange={onChange}
                layoutId={layoutId}
              />
            );
          })}
        </nav>
      </div>
    </div>
  );
};

interface TabsDropdownProps {
  ariaLabel: string;
  activeTab: string;
  onChange: (id: string) => void;
  tabs: Tab[];
}

const TabsDropdown: FC<TabsDropdownProps> = ({
  ariaLabel,
  activeTab,
  onChange,
  tabs,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const active = tabs.find((t) => t.id === activeTab) ?? tabs[0];

  const handleSelect = useCallback(
    (id: string) => {
      onChange(id);
      setOpen(false);
    },
    [onChange],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    function handleClick(e: MouseEvent) {
      if (
        e.target instanceof Node &&
        containerRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    }
    function handleKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  if (!active) {
    return null;
  }

  return (
    <div ref={containerRef} className="relative px-2 py-1.5 sm:hidden">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 border border-slate-300 bg-white/60 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600 transition-colors hover:border-emerald-500/60 dark:border-slate-700/60 dark:bg-slate-900/60 dark:text-emerald-300 dark:hover:border-emerald-400/60"
      >
        <span className="inline-flex items-center gap-1.5 truncate">
          <span aria-hidden className="text-emerald-500 dark:text-emerald-400">
            [
          </span>
          <span className="truncate">{active.label}</span>
          <span aria-hidden className="text-emerald-500 dark:text-emerald-400">
            ]
          </span>
          {typeof active.badgeCount === "number" && active.badgeCount > 0 ? (
            <span className="inline-flex min-w-[18px] items-center justify-center bg-emerald-500 px-1 font-mono text-[9px] font-black tabular-nums text-white dark:bg-emerald-400 dark:text-slate-950">
              {active.badgeCount > 99 ? "99+" : active.badgeCount}
            </span>
          ) : null}
        </span>
        <span
          aria-hidden
          className={`flex-shrink-0 text-[8px] transition-transform ${open ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, scale: 0.98, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -4 }}
            transition={{ bounce: 0, duration: 0.18, type: "spring" }}
            className="absolute left-2 right-2 top-full z-50 mt-1 max-h-[60vh] overflow-y-auto border border-slate-300 bg-white shadow-[4px_4px_0_rgba(15,23,42,0.18)] dark:border-emerald-400/30 dark:bg-slate-950/95 dark:shadow-[4px_4px_0_rgba(2,6,23,0.6)]"
          >
            <li className="border-b border-slate-200 px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-500 dark:border-slate-800/80">
              [ {ariaLabel} ]
            </li>
            {tabs.map((tab, index) => {
              const isActive = tab.id === activeTab;
              const previous = tabs[index - 1];
              const showGroup = tab.group && tab.group !== previous?.group;
              return (
                <DropdownTab
                  key={tab.id}
                  tab={tab}
                  isActive={isActive}
                  showGroup={showGroup}
                  handleSelect={handleSelect}
                />
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

interface DesktopTabProps {
  tab: Tab;
  active: boolean;
  showGroup: boolean | string | undefined;
  buttonRefs: { current: Record<string, HTMLButtonElement | null> };
  onChange: TabsProps["onChange"];
  layoutId: string;
}

function DesktopTab({
  tab,
  active,
  showGroup,
  buttonRefs,
  onChange,
  layoutId,
}: DesktopTabProps) {
  return (
    <Fragment key={tab.id}>
      {showGroup && (
        <span className="ml-1 self-center border-l border-slate-300/60 py-1 pl-3 pr-1 font-mono text-[8px] font-black uppercase tracking-[0.24em] text-slate-400/70 dark:border-slate-700/60 dark:text-slate-600">
          {tab.group}
        </span>
      )}
      <button
        ref={(el) => {
          buttonRefs.current[tab.id] = el;
        }}
        disabled={tab.disabled}
        onClick={() => {
          if (!tab.disabled) {
            onChange(tab.id);
          }
        }}
        className={`relative shrink-0 whitespace-nowrap px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] transition-colors ${
          tab.disabled ? "cursor-not-allowed" : ""
        }`}
        aria-current={active ? "page" : undefined}
        aria-disabled={tab.disabled || undefined}
      >
        <span
          className={`inline-flex items-center gap-1.5 ${getDesktopTabTextClass(tab.disabled, active)}`}
        >
          <span
            className={
              active
                ? "text-emerald-500 dark:text-emerald-400"
                : "text-slate-400 dark:text-slate-600"
            }
            aria-hidden
          >
            [
          </span>
          <span>{tab.label}</span>
          <span
            className={
              active
                ? "text-emerald-500 dark:text-emerald-400"
                : "text-slate-400 dark:text-slate-600"
            }
            aria-hidden
          >
            ]
          </span>
          {typeof tab.badgeCount === "number" && tab.badgeCount > 0 ? (
            <span
              className={`inline-flex min-w-[18px] items-center justify-center px-1 font-mono text-[9px] font-black tabular-nums ${
                active
                  ? "bg-emerald-500 text-white dark:bg-emerald-400 dark:text-slate-950"
                  : "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
              }`}
            >
              {tab.badgeCount > 99 ? "99+" : tab.badgeCount}
            </span>
          ) : null}
        </span>
        {active && (
          <motion.span
            layoutId={layoutId}
            className="absolute inset-x-2 -bottom-px h-0.5 bg-emerald-500 dark:bg-emerald-400"
            transition={{ bounce: 0.2, duration: 0.4, type: "spring" }}
          />
        )}
      </button>
    </Fragment>
  );
}

interface DropdownTabProps {
  tab: Tab;
  isActive: boolean;
  showGroup: boolean | string | undefined;
  handleSelect: TabsProps["onChange"];
}

function DropdownTab({
  tab,
  isActive,
  showGroup,
  handleSelect,
}: DropdownTabProps) {
  return (
    <Fragment key={tab.id}>
      {showGroup && (
        <li className="border-b border-slate-200 px-3 py-1 font-mono text-[9px] font-black uppercase tracking-[0.25em] text-slate-400 dark:border-slate-800/80 dark:text-slate-600">
          {tab.group}
        </li>
      )}
      <li role="option" aria-selected={isActive}>
        <button
          type="button"
          disabled={tab.disabled}
          onClick={() => {
            if (!tab.disabled) {
              handleSelect(tab.id);
            }
          }}
          className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left font-mono text-[11px] font-bold uppercase tracking-[0.2em] transition-colors ${getDropdownTabClass(tab.disabled, isActive)}`}
        >
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className={
                isActive
                  ? "text-emerald-500 dark:text-emerald-400"
                  : "text-slate-400 dark:text-slate-600"
              }
            >
              {isActive ? "✓" : " "}
            </span>
            <span>{tab.label}</span>
          </span>
          {typeof tab.badgeCount === "number" && tab.badgeCount > 0 ? (
            <span
              className={`inline-flex min-w-[18px] items-center justify-center px-1 font-mono text-[9px] font-black tabular-nums ${
                isActive
                  ? "bg-emerald-500 text-white dark:bg-emerald-400 dark:text-slate-950"
                  : "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
              }`}
            >
              {tab.badgeCount > 99 ? "99+" : tab.badgeCount}
            </span>
          ) : null}
        </button>
      </li>
    </Fragment>
  );
}
